import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, IDLE_FLAG_KEY } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState(() => {
    if (localStorage.getItem(IDLE_FLAG_KEY)) {
      localStorage.removeItem(IDLE_FLAG_KEY);
      return "You were signed out after 2 hours of inactivity. Sign in again to continue.";
    }
    return "";
  });
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState("");

  async function resend() {
    if (!email) { setErr("Enter your email above first, then tap resend."); return; }
    setErr(""); setResendMsg(""); setResending(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setResendMsg("If that email has an account, a fresh reset link is on its way (valid 60 min).");
    } catch {
      setResendMsg("Couldn't send just now — please try again.");
    } finally {
      setResending(false);
    }
  }

  async function onSubmit(e) {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
      nav("/", { replace: true });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#f5f2eb] text-[#0a0a0a] flex flex-col" data-testid="login-page">
      {/* Top masthead */}
      <div className="bm-thick" />
      <header className="px-4 sm:px-8 py-3 sm:py-4 flex items-center justify-between border-b border-[#0a0a0a] gap-3">
        <div className="bm-eyebrow">Est. 1999 &middot; Toronto</div>
        <div className="bm-eyebrow text-right">Vol. XXVII &middot; Trends Desk</div>
      </header>

      <div className="flex-1 grid lg:grid-cols-2">
        {/* Left column - brand wall (compact band on phones, shown after the form) */}
        <div className="relative overflow-hidden bg-[#0a0a0a] text-[#f5f2eb] p-8 lg:p-16 flex flex-col justify-between bm-grain order-2 lg:order-1" data-testid="login-brand-panel">
          <div>
            <div className="bm-eyebrow opacity-70">The Trends Desk</div>
            <img
              src="/brand/bm-wordmark.png"
              alt="Black Market"
              data-testid="login-logo-bm"
              className="mt-4 lg:mt-6 w-full max-w-[320px] lg:max-w-[560px] h-auto select-none login-logo"
              style={{ animationDelay: "0ms" }}
              draggable={false}
            />
            <div className="bm-headline italic font-normal text-[#c72c2c] text-3xl lg:text-5xl mt-2">insights.</div>
            <p className="mt-4 lg:mt-6 max-w-md text-sm lg:text-lg leading-relaxed opacity-80">
              A private trends desk for four vintage stores. Seasonally adjusted, quietly written,
              powered by three years of your own sales.
            </p>
          </div>

          <div className="mt-8 lg:mt-12 grid grid-cols-2 gap-6 lg:gap-8 items-end max-w-[360px] lg:max-w-[560px]" data-testid="login-store-logos">
            <img
              src="/brand/pb-logo.png"
              alt="Public Butter"
              data-testid="login-logo-pb"
              className="w-full h-auto select-none login-logo"
              style={{ animationDelay: "120ms" }}
              draggable={false}
            />
            <img
              src="/brand/dm-logo.png"
              alt="Dirty Mike's Thrift"
              data-testid="login-logo-dm"
              className="w-full h-auto select-none login-logo"
              style={{ animationDelay: "240ms" }}
              draggable={false}
            />
          </div>

          <div className="bm-eyebrow opacity-60 pt-8 lg:pt-10">
            347 Queen West &middot; 256 Queen West &middot; Kensington &middot; Dirty Mike's
          </div>
        </div>

        {/* Right column - login form (first on phones) */}
        <div className="p-6 sm:p-10 lg:p-20 flex items-center order-1 lg:order-2">
          <form onSubmit={onSubmit} className="w-full max-w-md space-y-6" data-testid="login-form">
            <div>
              <div className="bm-eyebrow">Sign in</div>
              <h2 className="bm-headline text-4xl mt-2">Staff entrance.</h2>
              <p className="mt-3 text-sm text-neutral-600">
                Four stores, three years of receipts, one desk.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="bm-eyebrow block mb-2">Email</label>
                <input
                  data-testid="login-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  className="w-full bg-transparent border-0 border-b-2 border-[#0a0a0a] focus:outline-none focus:border-[#c72c2c] py-3 text-lg font-mono-bm"
                  placeholder="you@blackmarkettoronto.com"
                />
              </div>
              <div>
                <label className="bm-eyebrow block mb-2">Password</label>
                <input
                  data-testid="login-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-transparent border-0 border-b-2 border-[#0a0a0a] focus:outline-none focus:border-[#c72c2c] py-3 text-lg font-mono-bm"
                  placeholder="Enter your password"
                />
              </div>
            </div>

            {err && (
              <div data-testid="login-error" className="text-sm text-[#c72c2c] font-mono-bm border-l-2 border-[#c72c2c] pl-3">
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full bg-[#0a0a0a] text-[#f5f2eb] py-4 bm-eyebrow hover:bg-[#c72c2c] transition-colors disabled:opacity-50"
            >
              {loading ? "Verifying…" : "Enter the Desk →"}
            </button>

            <div className="flex items-center justify-center gap-4 pt-2">
              <Link
                to="/forgot-password"
                data-testid="login-forgot"
                className="bm-eyebrow text-neutral-500 hover:text-[#c72c2c]"
              >
                Forgot password?
              </Link>
              <span className="text-neutral-300">&middot;</span>
              <button
                type="button"
                onClick={resend}
                disabled={resending}
                data-testid="login-resend"
                className="bm-eyebrow text-neutral-500 hover:text-[#c72c2c] disabled:opacity-50"
              >
                {resending ? "Sending…" : "Resend reset link"}
              </button>
            </div>
            {resendMsg && (
              <div data-testid="login-resend-msg" className="text-sm text-emerald-700 font-mono-bm text-center">
                {resendMsg}
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
